import React, { Suspense, useEffect, useState } from 'react';
import { FullPageLoading } from 'V2/Shared/FullPageLoading';
import { StepType } from 'V2/Apply';
import { Nullable } from 'V2/Types';
import { FeatureFlags } from 'V2/Utilities/FeatureFlags';
import { loadStep } from './stepModules';

const MONETIZATION_ENABLED_PARTNERS = new Set([
  'clearcover',
  'everquote',
  'smartfinancial',
  'smartfinancial.com insurance llc',
  'smartfinancial llc',
  'mt lead media',
]);

export const convertComponentName = (stepName: string) =>
  stepName
    .replace('primary_', '')
    .split('_')
    .map((word: string) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join('');

/** Step names from API that map to partner-agnostic component names */
const STEP_NAME_TO_COMPONENT: Record<string, string> = {
  quote_magic_coverage_level: 'CoverageLevel',
};

export const getStepComponentName = (stepName: string) =>
  STEP_NAME_TO_COMPONENT[stepName] ?? convertComponentName(stepName);

export const DynamicStepLoader: React.FC<
  StepType & { children?: React.ReactNode }
> = (props) => {
  const {
    status: { id: stepName, partner, attribution_data: attributionData },
    config: { features_by_key: featuresByKey },
  } = props;

  const { flexible_payments_enabled } = featuresByKey;
  const partnerNameFromAttribution =
    typeof attributionData?.partner_name === 'string'
      ? attributionData.partner_name.toLowerCase()
      : undefined;
  const partnerName =
    partner?.name?.toLowerCase() || partnerNameFromAttribution;

  const [LazyStep, SetLazyStep] = useState<Nullable<React.FC<StepType>>>(null);
  useEffect(() => {
    const componentName = getStepComponentName(stepName);
    const loader = loadStep(componentName);

    SetLazyStep(
      React.lazy(() =>
        (loader
          ? loader()
          : Promise.reject(new Error(`Step not found: ${componentName}`))
        ).catch((e: unknown) => {
          // When a deployment changes chunk hashes while a user is mid-flow,
          // the browser 404s on the old chunk URL. Auto-reload to pick up
          // the new build. See also the vite:preloadError listener in index.tsx.
          const chunkError =
            /Failed to fetch dynamically imported module/.test(`${e}`) ||
            /Loading [" "a-zA-Zchunk]+ [\d]+ failed/.test(`${e}`);
          if (chunkError) {
            window.location.reload();
            // Reload wins in practice, but surface a rejection so the
            // Suspense boundary doesn't try to render a half-loaded module.
            return Promise.reject(e);
          }
          return (
            loadStep('Default')?.() ??
            Promise.reject(new Error('Default step not found'))
          );
        })
      )
    );
  }, [stepName]);

  useEffect(() => {
    const baseMonetization = FeatureFlags.canView('monetization_widget');
    const baseNextinsure = FeatureFlags.canView('nextinsure_widget');
    const allowedPartner =
      !!partnerName && MONETIZATION_ENABLED_PARTNERS.has(partnerName);

    FeatureFlags.override({
      payment_flexibility: !!flexible_payments_enabled,
      monetization_widget: baseMonetization && allowedPartner,
      nextinsure_widget: baseNextinsure && allowedPartner,
    });
  }, [flexible_payments_enabled, partnerName, stepName]);

  return (
    <Suspense fallback={<FullPageLoading />}>
      {LazyStep && (
        <>
          <LazyStep {...props} />
          {props.children}
        </>
      )}
    </Suspense>
  );
};
